/** The Rules of Golf allow no more than 14 active clubs in a player's bag. */
export const MAX_BAG_CLUBS = 14;
export const MAX_BAG_CLUB_LABEL_LENGTH = 24;

export type BagClubCategory =
  | 'woods'
  | 'hybrids'
  | 'irons'
  | 'wedges'
  | 'putter';

export interface BagClubCatalogItem {
  key: string;
  label: string;
  category: BagClubCategory;
  catalogOrder: number;
}

const catalogLabelsByCategory = [
  {
    category: 'woods',
    labels: ['Driver', 'Mini Driver', '3W', '5W', '7W', '9W'],
  },
  {
    category: 'hybrids',
    labels: ['2H', '3H', '4H', '5H'],
  },
  {
    category: 'irons',
    labels: ['2i', '3i', '4i', '5i', '6i', '7i', '8i', '9i'],
  },
  {
    category: 'wedges',
    labels: ['PW', 'GW', '52', '54', '56', '58', '60'],
  },
  {
    category: 'putter',
    labels: ['Putter'],
  },
] as const satisfies ReadonlyArray<{
  category: BagClubCategory;
  labels: readonly string[];
}>;

/**
 * Removes presentation-only characters so a club keeps the same durable key
 * across case and spacing changes. The key is the server-side identity within
 * one user's bag.
 */
export function normalizeBagClubKey(value: string | null | undefined): string {
  return value?.trim().replace(/[^\dA-Za-z]/g, '').toLowerCase() ?? '';
}

export function normalizeBagClubLabel(
  value: string | null | undefined,
): string | null {
  const normalized = value?.trim().replace(/\s+/g, ' ') ?? '';
  return normalized || null;
}

export const BAG_CLUB_CATALOG: readonly BagClubCatalogItem[] =
  catalogLabelsByCategory.flatMap(({ category, labels }) =>
    labels.map((label) => ({
      key: normalizeBagClubKey(label),
      label,
      category,
    })),
  ).map((club, catalogOrder) => ({ ...club, catalogOrder }));

export const DEFAULT_BAG_CLUB_LABELS = [
  'Driver',
  '3W',
  '5W',
  '4H',
  '5i',
  '6i',
  '7i',
  '8i',
  '9i',
  'PW',
  '52',
  '56',
  '60',
  'Putter',
] as const;

export interface BagClub {
  /**
   * Server UUID for a saved club. Suggested defaults use null until the user
   * confirms the bag for the first time.
   */
  id: string | null;
  /** Durable identity within a user's bag, such as `driver` or `7i`. */
  key: string;
  label: string;
  sortOrder: number;
}

/** GET /v1/me/bag and successful PUT /v1/me/bag. */
export interface UserBagResponse {
  clubs: BagClub[];
  setupCompletedAt: string | null;
  updatedAt: string | null;
  /**
   * True when no saved bag exists yet and `clubs` contains the suggested
   * defaults. Clients should still require the user to confirm that selection.
   */
  usesDefaultClubs: boolean;
}

/** PUT /v1/me/bag */
export interface ReplaceUserBagRequest {
  clubs: string[];
}

export type BagClubValidationResult =
  | { ok: true; clubs: BagClub[] }
  | { ok: false; error: string };

const catalogByKey = new Map(
  BAG_CLUB_CATALOG.map((club) => [club.key, club] as const),
);

/**
 * Validates a complete replacement bag. Known club labels are canonicalized,
 * while valid custom labels retain the user's spelling.
 */
export function validateAndNormalizeBagClubs(
  value: unknown,
): BagClubValidationResult {
  if (!Array.isArray(value)) {
    return { ok: false, error: 'clubs must be an array' };
  }
  if (value.length === 0) {
    return { ok: false, error: 'Keep at least one active club in your bag' };
  }
  if (value.length > MAX_BAG_CLUBS) {
    return {
      ok: false,
      error: `Keep your active bag to ${MAX_BAG_CLUBS} clubs or fewer`,
    };
  }

  const clubs: BagClub[] = [];
  const seenKeys = new Set<string>();

  for (const valueAtIndex of value) {
    if (typeof valueAtIndex !== 'string') {
      return { ok: false, error: 'Every club must be a text label' };
    }

    const normalizedLabel = normalizeBagClubLabel(valueAtIndex);
    if (!normalizedLabel) {
      return { ok: false, error: 'Club names cannot be empty' };
    }
    if (normalizedLabel.length > MAX_BAG_CLUB_LABEL_LENGTH) {
      return {
        ok: false,
        error: `Club names must be ${MAX_BAG_CLUB_LABEL_LENGTH} characters or fewer`,
      };
    }

    const key = normalizeBagClubKey(normalizedLabel);
    if (!key) {
      return {
        ok: false,
        error: 'Club names must include at least one letter or number',
      };
    }
    if (seenKeys.has(key)) {
      return { ok: false, error: 'Bag clubs must be unique' };
    }

    seenKeys.add(key);
    clubs.push({
      id: null,
      key,
      label: catalogByKey.get(key)?.label ?? normalizedLabel,
      sortOrder: clubs.length,
    });
  }

  return { ok: true, clubs };
}

export function getDefaultBagClubs(): BagClub[] {
  const result = validateAndNormalizeBagClubs([...DEFAULT_BAG_CLUB_LABELS]);
  if (!result.ok) {
    throw new Error(`Invalid default bag configuration: ${result.error}`);
  }
  return result.clubs;
}
